import { ApiError, unwrap, type components } from "@wilco/api";
import { api } from "../auth.ts";
import { partnerName, productInfo, productInfos, warehouses, type ProductInfo } from "../lookups.ts";
import type {
  CountBody,
  CountOutcome,
  CountView,
  IssueBody,
  IssueCard,
  IssueResult,
  IssueWriteOffBody,
  NewRequestBody,
  ProductRef,
  ProductStock,
  ReceiveBody,
  ReceiveResult,
  RequestSummary,
  ScanResult,
  ShelfView,
  Suggestions,
  Today,
  WarehouseCode,
  WarehouseRef,
} from "./types.ts";

/*
 * Склад на REST из contracts/openapi.json (модуль inventory, PR #38).
 * Ответы контракта переводятся в вид, который ждут экраны (types.ts):
 * названия товаров из справочника, склады по id, поставщики из partners.
 * Чего контракт не даёт, экран не показывает (поля null).
 */
type S = components["schemas"];

const ref = (p: ProductInfo): ProductRef => ({ id: p.id, sku: p.sku, name: p.name, uom: p.uom, consumable: p.consumable });
const shelfCode = (l: S["InventoryLocation"]) => l.barcode ?? l.name;
const today = () => new Date().toISOString().slice(0, 10);

async function warehouseRef(id: string): Promise<WarehouseRef> {
  const w = (await warehouses()).find((x) => x.id === id);
  return { code: (w?.code ?? "MAIN") as WarehouseCode, name: w?.name ?? id, activity: w?.kind !== "MAIN" && w?.kind !== "main" };
}

async function warehouseByCode(code: WarehouseCode): Promise<S["InventoryWarehouse"]> {
  const w = (await warehouses()).find((x) => x.code === code);
  if (!w) throw new ApiError(404, { type: "urn:wilco:rule:inventory.not_found", title: "not found", status: 404, code: "inventory.not_found" });
  return w;
}

const scanShelf = (code: string) => unwrap(api.GET("/api/v1/inventory/shelves/scan", { params: { query: { code } } }));
const scanProduct = (code: string) => unwrap(api.GET("/api/v1/inventory/products/scan", { params: { query: { code } } }));

async function shelfView(card: S["InventoryShelfCard"]): Promise<ShelfView> {
  const productId = card.product?.id ?? card.shelf.productId ?? "";
  const info = await productInfo(productId, card.product?.name);
  const reserved = card.reservedBy.reduce((a, r) => a + r.qty, 0);
  return {
    id: card.shelf.id,
    code: shelfCode(card.shelf),
    warehouse: { code: "MAIN", name: "Основний", activity: false },
    product: ref(info),
    onShelf: card.onShelf,
    reserved,
    reservedBy: [...new Set(card.reservedBy.map((r) => r.request))],
    expected: card.expected.map((e) => ({ order: e.order, supplier: "", due: e.scheduledOn, qty: e.qty, requests: [] })),
    expectedTotal: card.expected.reduce((a, e) => a + e.qty, 0),
    onLine: info.consumable ? card.onLine : null,
    lastCount: card.lastCountedOn ?? null,
  };
}

export const stockApi = {
  /** Код со сканера: полка, иначе товар по артикулу. */
  scan: async (code: string): Promise<ScanResult> => {
    try {
      const card = await scanShelf(code);
      return { kind: "shelf", code: shelfCode(card.shelf) };
    } catch (e) {
      if (!(e instanceof ApiError) || e.status !== 404) throw e;
      const product = await scanProduct(code);
      return { kind: "product", sku: (await productInfo(product.product.id, product.product.name)).sku };
    }
  },

  shelf: async (code: string): Promise<ShelfView> => shelfView(await scanShelf(code)),

  countView: async (code: string): Promise<CountView> => {
    const v = await shelfView(await scanShelf(code));
    // Остаток в ответе скана есть, но подсчёт вслепую его не показывает (INV-30).
    return { id: v.id, code: v.code, product: v.product, lastCount: v.lastCount, pending: false };
  },

  receive: async (shelfId: string, body: ReceiveBody): Promise<ReceiveResult> => {
    const r = await unwrap(api.POST("/api/v1/inventory/shelves/{id}/receipts", { params: { path: { id: shelfId } }, body }));
    return {
      received: r.received,
      expectedLeft: r.stillExpected,
      reservedFor: r.reservedForRequests > 0 ? [{ request: "", warehouse: "", qty: r.reservedForRequests }] : [],
      free: r.received - r.reservedForRequests,
      withoutOrder: body.withoutOrder,
      priceMissing: false,
    };
  },

  count: async (shelfId: string, body: CountBody): Promise<{ outcome: CountOutcome }> => {
    const r = await unwrap(api.POST("/api/v1/inventory/shelves/{id}/counts", { params: { path: { id: shelfId } }, body: { counted: body.qty, confirmed: body.confirmed } }));
    return { outcome: r.outcome as CountOutcome };
  },

  /** «Видати і списати» (H2): выдача с полки на «Лінію» или «Робочу зону» склада с причиной. */
  issueWriteOff: async (shelf: ShelfView, body: IssueWriteOffBody): Promise<{ qty: number; warehouse: string }> => {
    const w = await warehouseByCode(body.warehouse);
    const place = w.locations.find((l) => l.role === "LINE") ?? w.locations.find((l) => l.role === "WORK_ZONE") ?? w.locations.find((l) => l.role === "PACKING");
    await unwrap(
      api.POST("/api/v1/inventory/issues", {
        body: { fromShelfId: shelf.id, productId: shelf.product.id, qty: body.qty, reason: body.reason, toLocationId: place?.id ?? "" },
      }),
    );
    return { qty: body.qty, warehouse: w.name };
  },

  product: async (code: string): Promise<ProductStock> => {
    const card = await scanProduct(code);
    const info = await productInfo(card.product.id, card.product.name);
    const main = (await warehouses()).find((w) => w.kind.toUpperCase() === "MAIN");
    return {
      product: ref(info),
      shelves: card.shelves.map((l) => ({ code: shelfCode(l), onShelf: null })),
      stock: [{ warehouse: { code: "MAIN", name: main?.name ?? "Основний", activity: false }, onHand: card.onHand, reserved: card.reserved }],
    };
  },

  products: async (q: string): Promise<ProductRef[]> => {
    const found = await unwrap(api.GET("/api/v1/catalog/products/search", { params: { query: { q, limit: 10 } } }));
    return found.map((m) => ({
      id: m.product.id,
      sku: m.product.code ?? m.product.id,
      name: m.product.version ? `${m.product.name} (${m.product.version})` : m.product.name,
      uom: "шт",
      consumable: m.product.tags.includes("CONSUMABLE"),
    }));
  },

  /** «Склад сьогодні»: ожидаемые поставки, очередь «Видати на склад», подсчёт. */
  today: async (): Promise<Today> => {
    const [orders, queued, countQueue, pending] = await Promise.all([
      unwrap(api.GET("/api/v1/purchasing/orders", { params: { query: { filter: ["NOT_RECEIVED"] } } })),
      unwrap(api.GET("/api/v1/inventory/requests", { params: { query: { status: "QUEUED" } } })),
      unwrap(api.GET("/api/v1/inventory/counts/queue")),
      unwrap(api.GET("/api/v1/inventory/counts/pending")),
    ]);
    const infos = await productInfos([
      ...orders.flatMap((o) => o.lines.map((l) => l.productId)),
      ...countQueue.map((l) => l.productId).filter((x): x is string => Boolean(x)),
    ]);
    const now = today();
    const receipts = await Promise.all(
      orders
        .filter((o) => o.state === "CONFIRMED")
        .map(async (o) => {
          const due = (o.expectedArrival ?? o.createdAt).slice(0, 10);
          return {
            id: o.id,
            order: o.number,
            supplier: await partnerName(o.supplierId),
            due,
            late: due < now,
            lines: o.lines.map((l) => ({ product: ref(infos.get(l.productId) ?? { id: l.productId, sku: l.productId, name: l.productId, uom: "шт", consumable: false }), shelf: null, qty: l.qty })),
          };
        }),
    );
    const issues = await Promise.all(queued.map(issueCard));
    const due = countQueue.map((l) => {
      const info = l.productId ? infos.get(l.productId) : undefined;
      return { code: shelfCode(l), product: ref(info ?? { id: l.productId ?? "", sku: "", name: "", uom: "шт", consumable: false }), lastCount: null };
    });
    return { receipts: receipts.sort((a, b) => a.due.localeCompare(b.due)), issues, counts: { due, pending: pending.length } };
  },

  issueRequest: async (id: string): Promise<IssueCard> => issueCard(await unwrap(api.GET("/api/v1/inventory/requests/{id}", { params: { path: { id } } }))),

  /** «Видати»: каждая строка своей выдачей с полки; недобор остаётся в заявке. */
  issue: async (id: string, body: IssueBody): Promise<IssueResult> => {
    const request = await unwrap(api.GET("/api/v1/inventory/requests/{id}", { params: { path: { id } } }));
    let issued = 0;
    for (const l of body.lines.filter((x) => x.qty > 0)) {
      const line = request.lines.find((x) => x.id === l.id);
      const shelf = line?.shelves[0];
      if (!line || !shelf) continue;
      await unwrap(api.POST("/api/v1/inventory/requests/{id}/lines/{lineId}/issues", { params: { path: { id, lineId: l.id } }, body: { fromShelfId: shelf.id, qty: l.qty } }));
      issued += l.qty;
    }
    const after = await unwrap(api.GET("/api/v1/inventory/requests/{id}", { params: { path: { id } } }));
    const infos = await productInfos(after.lines.map((x) => x.product.id));
    const short = after.lines.filter((x) => x.remaining > 0).map((x) => ({ sku: infos.get(x.product.id)?.sku ?? x.product.name, qty: x.remaining }));
    return { number: after.number, issued, short };
  },

  requests: async (warehouse?: WarehouseCode): Promise<RequestSummary[]> => {
    const all = await unwrap(api.GET("/api/v1/inventory/requests"));
    const list = await Promise.all(all.map(requestSummary));
    return warehouse ? list.filter((r) => r.warehouse.code === warehouse) : list;
  },

  /** Подсказки дозаказа склада: «Часто замовляєте» и совет по каждой позиции. */
  suggestions: async (warehouse: WarehouseCode): Promise<Suggestions> => {
    const w = await warehouseByCode(warehouse);
    const frequent = await unwrap(api.GET("/api/v1/purchasing/frequent"));
    const infos = await productInfos(frequent.map((f) => f.productId));
    const advice = await Promise.all(
      frequent.map(async (f) => ({ f, a: await unwrap(api.GET("/api/v1/purchasing/advice", { params: { query: { warehouseId: w.id, productId: f.productId } } })) })),
    );
    const info = (id: string) => infos.get(id) ?? { id, sku: id, name: id, uom: "шт", consumable: false };
    return {
      warehouse: { code: warehouse, name: w.name, activity: true },
      suggestions: advice
        .filter(({ a }) => a.qty > 0)
        .map(({ f, a }) => ({
          product: ref(info(f.productId)),
          dailyUse: a.dailyUse ?? null,
          daysOfStock: a.daysOfStock ?? null,
          available: a.available,
          leadDays: 0,
          safetyDays: 0,
          targetDays: 0,
          min: null,
          qty: a.qty,
          reason: a.belowMinimum ? ("below_min" as const) : ("below_lead" as const),
        })),
      frequent: frequent.map((f) => ({ product: ref(info(f.productId)), times: f.times, lastQty: f.lastQty })),
    };
  },

  createRequest: async (body: NewRequestBody): Promise<RequestSummary> => {
    const w = await warehouseByCode(body.warehouse);
    const created = await unwrap(
      api.POST("/api/v1/inventory/requests", {
        body: { warehouseId: w.id, lines: body.lines.map((l) => ({ productId: l.sku, qty: l.qty, needDate: body.needDate })) },
      }),
    );
    return requestSummary(created);
  },
};

async function issueCard(r: S["InventoryRequest"]): Promise<IssueCard> {
  const infos = await productInfos(r.lines.map((l) => l.product.id));
  const need = r.needDate ?? r.lines[0]?.needDate ?? today();
  return {
    id: r.id,
    number: r.number,
    warehouse: await warehouseRef(r.warehouseId),
    needDate: need,
    late: need < today(),
    order: r.order ?? null,
    lines: r.lines
      .filter((l) => l.remaining > 0)
      .map((l) => ({
        id: l.id,
        product: ref(infos.get(l.product.id) ?? { id: l.product.id, sku: l.product.id, name: l.product.name, uom: "шт", consumable: false }),
        shelf: l.shelves[0] ? shelfCode(l.shelves[0]) : null,
        shelfId: l.shelves[0]?.id ?? null,
        // К выдаче жёсткий резерв строки; сколько на полке, ответ не говорит.
        qty: l.reserved > 0 ? l.reserved : l.remaining,
        onShelf: l.reserved > 0 ? l.reserved : l.remaining,
      })),
  };
}

const STATES: Record<string, RequestSummary["state"]> = { OPEN: "open", QUEUED: "queued", DONE: "done", CANCELLED: "cancelled" };
const SOURCES: Record<string, RequestSummary["source"]> = { MANUAL: "manual", SHORTAGE: "shortage", MINIMUM: "minimum", SUGGESTION: "suggestion" };

async function requestSummary(r: S["InventoryRequest"]): Promise<RequestSummary> {
  return {
    id: r.id,
    number: r.number,
    warehouse: await warehouseRef(r.warehouseId),
    state: STATES[r.status] ?? "open",
    source: SOURCES[r.source] ?? "manual",
    needDate: r.needDate ?? r.lines[0]?.needDate ?? today(),
    order: r.order ?? null,
    lines: r.lines.length,
  };
}

export { problemCode } from "../rest.ts";
